import { FC, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import { Modal, SelectionCard } from "../../common";
import { uiConstants } from "../../../constants";
import { selectCurrentUser } from "../../../store/reducers/user";
import { OnmetaStatus, Wallet } from "../../../types/WalletTypes";
import { isMobileMedia } from "../../../utils/media";
import {
  validateWalletOnmetaStatus,
  StatusMessageValue,
} from "../../../utils/wallets";

type Props = {
  value?: Wallet;
  values: Wallet[];
  type?: "fiat" | "crypto";
  onSelect: (wallet: Wallet) => void;
  onCreate: () => void;
  readonly?: boolean;
  fullWidth?: boolean;
};

const typeLabel = {
  crypto: "wallet",
  fiat: "bank account",
};

export const WalletDropdown: FC<Props> = ({
  value,
  values = [],
  type = "crypto",
  onSelect,
  onCreate,
  readonly = false,
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    kyc_complete_and_approved,
    is_onmeta_eligible,
    onmeta_kyc_complete_and_approved,
  } = useSelector(selectCurrentUser);

  const walletCards = useMemo(
    () =>
      values.map((wallet) => (
        <SelectionCard
          icon="Bank"
          key={wallet.id}
          title={`${wallet.chain} - ${wallet.name}`}
          value={wallet.address!}
          onClick={() => {
            onSelect(wallet);
            setIsOpen(false);
          }}
          className={
            value?.address === wallet.address
              ? "outline-2 outline-green-300 bg-green-50"
              : ""
          }
        />
      )),
    [onSelect, value?.address, values]
  );

  const addWallet = useMemo(
    () => (
      <SelectionCard
        icon="Add"
        title={`Add new ${typeLabel[type]}`}
        value={`to receive your funds`}
        onClick={onCreate}
      />
    ),
    [onCreate, type]
  );

  const uiStatus = validateWalletOnmetaStatus(value);
  const _uiStatus = !!uiStatus ? (uiStatus as StatusMessageValue) : undefined;
  const uiStatusStyles = _uiStatus ? uiConstants[_uiStatus[0]] : undefined;

  const enabled =
    !readonly &&
    [OnmetaStatus.FAILED, OnmetaStatus.N_A, OnmetaStatus.SUCCESS].includes(
      value?.onmeta_status || OnmetaStatus.N_A
    );

  return (
    <div className={`${fullWidth ? "w-full" : "max-w-[528px]"} relative`}>
      <div className="flex flex-col gap-2">
        {value ? (
          <SelectionCard
            icon="Bank"
            title={`${value.chain} - ${value.name}`}
            value={value.address!}
            onClick={() => enabled && setIsOpen(!isOpen)}
            className="outline-2 outline-green-300 bg-green-50"
            uiStatus={_uiStatus?.[0]}
          />
        ) : !values || values.length === 0 ? (
          addWallet
        ) : (
          <SelectionCard
            icon="Chevron-down"
            title={`Select ${typeLabel[type]}`}
            value={`Select the ${typeLabel[type]} to receive your funds`}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}

        {_uiStatus && (
          <div className={uiStatusStyles?.textColor}>
            {typeof _uiStatus[1] === "string"
              ? _uiStatus[1]
              : _uiStatus[1](
                  kyc_complete_and_approved &&
                    (is_onmeta_eligible
                      ? onmeta_kyc_complete_and_approved
                      : true)
                )}
          </div>
        )}
      </div>

      {isMobileMedia() ? (
        <Modal
          close={() => setIsOpen(false)}
          isOpen={isOpen}
          title={`Select ${typeLabel[type]}`}
        >
          <div className="flex flex-col gap-y-2">
            {walletCards}
            {addWallet}
          </div>
        </Modal>
      ) : (
        isOpen && (
          <div className="flex flex-col gap-y-2 p-4 w-full border-gray-50 border rounded-md absolute z-[60] bg-white top-24 left-0 outline-gray-100 shadow">
            {walletCards}
            {addWallet}
          </div>
        )
      )}
    </div>
  );
};
